import React, { useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import './index.css';
import {
  ThemeProvider, Container, Typography, Card, CardContent, Button, LinearProgress,
  Box, Select, MenuItem, FormControl, InputLabel, Tabs, Tab, useMediaQuery,
  Chip, Stack, Divider, Paper, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Switch, Tooltip, Avatar, InputAdornment
} from '@mui/material';
import {
  CloudUpload, CheckCircle, Warning, AdminPanelSettings, Language, Translate,
  Speed, AutoAwesome, PictureAsPdf, Description, Download, Person, WorkspacePremium,
  History, Refresh, Visibility, VisibilityOff, Transform, Article, VolumeUp, Slideshow, StopCircle, OpenInNew
} from '@mui/icons-material';
import theme from './theme';
import { Helmet } from '@dr.pogodin/react-helmet';

// ============================================================
// BILINGUAL UI STRINGS
// ============================================================
const UI = {
  en: {
    appName: 'LeaTonga',
    tagline: 'Professional document translation · Tongan, Sāmoan, Fijian, Māori & more',
    fastTab: 'Fast Translation',
    perfectTab: 'Perfect Formatting',
    metricsTab: 'My Account',
    subscribeTab: 'Subscribe',
    yourAccount: 'Your Account',
    emailLabel: 'Email Address',
    emailPlaceholder: 'you@example.com',
    registerBtn: 'Register / Continue',
    registering: 'Registering...',
    accountNote: 'Enter your email to register or access your existing account. Your email activates your plan instantly after payment.',
    startFast: '⚡ Start Fast Translation',
    startPerfect: '✨ Start Perfect Translation',
    dropZone: 'Drop your DOCX file here',
    dropZoneClick: 'or click to browse',
    fileTypes: 'DOCX only · Max 50MB  |  For PDF files, use free ilovepdf.com to convert to DOCX first',
    translationComplete: 'Translation Complete!',
    download: 'Download Translated Document',
    fromLang: 'Translate From',
    toLang: 'Translate To',
    plan: 'Plan',
    processing: 'Processing...',
    preservesFormatting: 'Preserves all formatting — colors, fonts, tables, images, headers & footers',
    perfectTitle: 'Perfect Formatting',
    fastTitle: 'Fast Translation',
    fastNote: 'Quick translation with essential formatting. Best for text-heavy documents.',
    perfectNote: 'Full formatting preserved. Best for complex documents with tables, images & precise layout.',
    subscriptionPlans: 'Subscription Plans',
    free: 'Free',
    monthly: 'Monthly',
    annualLabel: 'Annual',
    annualSave: 'Save 33%',
    subscribeBtnMonthly: 'Subscribe Monthly',
    subscribeBtnAnnual: 'Subscribe Annually',
    currentPlan: 'Current Plan',
    popular: 'POPULAR',
    bestValue: 'BEST VALUE',
    metricsTitle: 'Usage & Account',
    totalSessions: 'Total Translations',
    totalChars: 'Characters Translated',
    totalPages: 'Pages Translated',
    fastDone: 'Fast Translations',
    perfectDone: 'Perfect Translations',
    renewsOn: 'Subscription renews',
    noMetrics: 'No activity yet — upload a document to get started!',
    convertingPdf: 'Converting PDF to DOCX for translation...',
    proceedAnyway: 'Proceed Anyway',
    cancel: 'Cancel',
    docAssessment: 'Document Assessment',
    upgradeForFull: 'Upgrade for full document translation',
    contactLarge: 'For books or large documents (200+ pages):',
    poweredBy: 'Powered by TongaMarket.com',
    developedBy: 'Developed by Stevenesau.com',
    copyright: '© 2026 LeaTonga · All rights reserved · Mālō \'aupito',
    emailSupport: 'Email Support',
    whatsapp: 'WhatsApp: +676 771 1961',
    premiumIncludes: 'Premium includes:',
    goldIncludes: 'Gold includes:',
    loginPrompt: 'Please register with your email first.',
    prefilledNote: 'Your email will be pre-filled at checkout for instant plan activation after payment.',
    subscriptionNote: 'Subscriptions are securely processed on TongaMarket.com with automatic activation.',
    registerFirst: 'Register your email first to start translating',
    pdfConverting: 'Your PDF is being converted automatically — no external tools needed!',
    recommended: 'Recommended',
    chars: 'characters',
    pages: 'pages',
    estTime: 'Estimated time',
    uploading: 'Uploading & processing...',
    planFree: 'FREE',
    planPremium: 'PREMIUM',
    planGold: 'GOLD',
    langToggle: 'Tongan UI',
    dropActive: 'Drop file here!',
    fileSelected: 'File selected:',
    removeFile: 'Remove',
    planLimits: 'Translation limits',
    fastRemaining: 'Fast translations left',
    perfectRemaining: 'Perfect translations left',
    perCharLimit: 'characters per document',
    perPageLimit: 'pages per document',
    recentTranslations: 'Recent Translations',
    noRecentJobs: 'No recent translations yet.',
    redownload: 'Re-download',
    jobCompleted: 'Completed',
    jobProcessing: 'Processing',
    jobFailed: 'Failed',
    refreshJobs: 'Refresh',
    pdfTab: 'PDF to DOCX',
    enhanceTab: 'Doc Enhancer',
    pdfTitle: 'Convert PDF to DOCX',
    pdfDesc: 'Convert any PDF to DOCX preserving all formatting, images, tables, graphs, fonts and colours. Then use Fast/Perfect Translation to translate it.',
    pdfDropZone: 'Drop your PDF file here',
    pdfFileTypes: 'PDF only · Max 50MB',
    pdfConvertBtn: 'Convert PDF to DOCX',
    pdfConvertProgress: 'Converting PDF...',
    pdfDone: 'Conversion Complete!',
    pdfDownload: 'Download DOCX File',
    pdfNextStep: 'Your DOCX is ready! Now use Fast Translation or Perfect Formatting to translate it.',
    enhanceTitle: 'Document Enhancer',
    enhanceDesc: 'Convert your translated DOCX to professional HTML with Tongan KJV Bible verse tooltips (hover to read). Download as PDF or create a PowerPoint presentation.',
    enhanceDropZone: 'Drop your translated DOCX here',
    enhanceConvertBtn: 'Enhance Document',
    enhancing: 'Enhancing...',
    enhanceDone: 'Document Enhanced!',
    viewHtmlDoc: 'View HTML Document',
    downloadPdfDoc: 'Download as PDF',
    generateAudioBtn: 'Generate Audio',
    stopAudioBtn: 'Stop Audio',
    audioGenerating: 'Generating audio...',
    createPptxBtn: 'Create PowerPoint',
    presenterLabel: 'Your Name (Presenter)',
    audienceLabel: 'Audience / Target Group',
    pptxTitleLabel: 'Presentation Title',
    generatePptxBtn: 'Generate Presentation',
    pptxGenerating: 'Creating PowerPoint...',
    pdfGenerating: 'Generating PDF...',
    bibleVerseHint: 'Hover or click on red Bible references to see Tongan KJV verses',
  },
  to: {
    appName: 'LeaTonga',
    tagline: "'Ulungaanga 'o e liliu tohi · Tonga, Sāmoa, Fisi, Māori & lahi ange",
    fastTab: 'Liliu Vave',
    perfectTab: 'Fakatonutonu Kakato',
    metricsTab: 'Ko Au',
    subscribeTab: 'Tohi Kotoa',
    yourAccount: "Ho'o Akauni",
    emailLabel: 'Tohi Imeli',
    emailPlaceholder: 'ho.imeli@imeli.com',
    registerBtn: 'Lesisita / Hokohoko Atu',
    registering: "'Oku lesisita...",
    accountNote: "Tuku ho'o imeli ke lesisita pe ke ma'u ho'o akauni. Ko ho'o imeli 'oku fakatonutonu ai ho'o palani vave hili 'a e totongi.",
    startFast: '⚡ Kamata Liliu Vave',
    startPerfect: '✨ Kamata Liliu Kakato',
    dropZone: "Tuku hifo ho'o faile DOCX heni",
    dropZoneClick: "pe ulu'i ke sio",
    fileTypes: "DOCX pe · Lahi taha 50MB  |  Ki he PDF, ngāue'aki ilovepdf.com (free) ke liliu ki he DOCX pea 'aupulouti heni",
    translationComplete: "Kuo Kakato 'a e Liliu!",
    download: "Hilifaki 'a e Tohi Kuo Liliu",
    fromLang: 'Liliu Mei',
    toLang: 'Liliu Ki',
    plan: 'Palani',
    processing: "'Oku ngāue'aki...",
    preservesFormatting: "'Oku tauhi 'a e ngaahi kala, faito'o, ngaahi tepu, ngaahi 'ata, tauhulu mo ngaahi va'alalo",
    perfectTitle: 'Fakatonutonu Kakato',
    fastTitle: 'Liliu Vave',
    fastNote: "Liliu vave 'o tauhi fakatonutonu tauhoa. Lelei ki he ngaahi tohi tohi.",
    perfectNote: "Ko 'ene tauhi fakatonutonu kakato. Lelei ki he ngaahi tohi tokolahi, tepu, 'ata & fakatonutonu totonu.",
    subscriptionPlans: 'Ngaahi Palani Tohi Kotoa',
    free: "'Ataa",
    monthly: 'Mahina kotoa',
    annualLabel: "Ta'u kotoa",
    annualSave: 'Sēivi 33%',
    subscribeBtnMonthly: 'Tohi Kotoa Mahina',
    subscribeBtnAnnual: "Tohi Kotoa Ta'u",
    currentPlan: 'Palani Lolotonga',
    popular: "FAKA'AMU'I TAHA",
    bestValue: "'AONGA TAHA",
    metricsTitle: "Ngāue mo e Akauni",
    totalSessions: 'Ngaahi Liliu Kotoa',
    totalChars: "Ngaahi Tefito'i Lea",
    totalPages: 'Ngaahi Peesi',
    fastDone: 'Ngaahi Liliu Vave',
    perfectDone: 'Ngaahi Liliu Kakato',
    renewsOn: "'Oku fakafo'ou 'a e tohi kotoa",
    noMetrics: "Kuo 'ikai ha ngāue — hilifaki hake ha tohi ke kamata!",
    convertingPdf: 'Ko e liliu \u02bbo e PDF ki DOCX ki he liliu...',
    proceedAnyway: 'Hokohoko Atu',
    cancel: 'Fakatāpuni',
    docAssessment: "Fakamatala ki he Tohi",
    upgradeForFull: "Fakalelei'i ho'o palani ki he liliu kakato",
    contactLarge: "Ki he ngaahi tohi lahi (200+ peesi):",
    poweredBy: "Ko e mālohi mei he TongaMarket.com",
    developedBy: "Na'e ngaohi 'e Stevenesau.com",
    copyright: "© 2026 LeaTonga · Ngaahi totonu kotoa 'oku tauhi · Mālō 'aupito",
    emailSupport: 'Poupou Imeli',
    whatsapp: 'WhatsApp: +676 771 1961',
    premiumIncludes: "'Oku kau 'i he Premium:",
    goldIncludes: "'Oku kau 'i he Gold:",
    loginPrompt: "Kataki lesisita ho'o imeli ki mu'a.",
    prefilledNote: "Ko ho'o imeli 'e tāpuni 'i he checkout ki he fakatonutonu vave hili 'a e totongi.",
    subscriptionNote: "Ko e ngaahi tohi kotoa 'oku fai fakalelei 'i he TongaMarket.com 'o fakatonutonu vave.",
    registerFirst: "Lesisita ho'o imeli ki mu'a ke liliu ha tohi",
    pdfConverting: "'Oku liliu fakatokelau 'a e PDF — 'ikai ngaue'aki ha founga kehe!",
    recommended: "Fakafeohi'aki",
    chars: "ngaahi tefito'i lea",
    pages: 'peesi',
    estTime: "Taimi 'amanaki",
    uploading: "'Oku hilifaki hake & ngāue'aki...",
    planFree: "'ATAA",
    planPremium: 'PREMIUM',
    planGold: 'GOLD',
    langToggle: 'UI Faka-Tonga',
    dropActive: 'Tuku hifo \u02bba e faile!',
    fileSelected: "'Oku 'i ai ha faile:",
    removeFile: 'Toho',
    planLimits: "Ngaahi tefitoʻi lea fakalelei",
    fastRemaining: "Ngaahi liliu vave toe",
    perfectRemaining: "Ngaahi liliu kakato toe",
    perCharLimit: "ngaahi tefito'i lea ki he tohi taki taha",
    perPageLimit: "ngaahi peesi ki he tohi taki taha",
    recentTranslations: 'Ngaahi Liliu Talu Ni',
    noRecentJobs: "'Ikai ha ngaahi liliu kuo fai.",
    redownload: 'Hilifaki Toe',
    jobCompleted: 'Kakato',
    jobProcessing: "'Oku ngāue'aki",
    jobFailed: "Kuo hala",
    refreshJobs: 'Fakafo\'ou',
    pdfTab: 'PDF ki DOCX',
    enhanceTab: 'Fakalahi Tohi',
    pdfTitle: 'Fakalelei PDF ki DOCX',
    pdfDesc: "Fakalelei ha PDF ki DOCX 'o tauhi 'a e fakatonutonu, ngaahi 'ata, tepu, kalafi, faito'o mo e ngaahi kala. Pea ngaue'aki 'a e Liliu Vave pe Fakatonutonu Kakato ke liliu.",
    pdfDropZone: "Tuku hifo ho'o faile PDF heni",
    pdfFileTypes: 'PDF pe · Lahi taha 50MB',
    pdfConvertBtn: 'Fakalelei PDF ki DOCX',
    pdfConvertProgress: "'Oku fakalelei PDF...",
    pdfDone: "Kuo Kakato 'a e Fakalelei!",
    pdfDownload: "Hilifaki 'a e Faile DOCX",
    pdfNextStep: "Ko ho'o DOCX kuo 'i ai! Ngaue'aki 'a e Liliu Vave pe Fakatonutonu Kakato ke liliu.",
    enhanceTitle: 'Fakalahi Tohi',
    enhanceDesc: "Fakalelei ho'o DOCX kuo liliu ki ha HTML 'aonga mo e ngaahi veesi Tohitapu Faka-Tonga KJV (move 'a e mausi ke sio ki he veesi). Tauhi ko ha PDF pe fakatupu ha PowerPoint.",
    enhanceDropZone: "Tuku hifo ho'o DOCX kuo liliu heni",
    enhanceConvertBtn: "Fakalahi 'a e Tohi",
    enhancing: "'Oku fakalahi...",
    enhanceDone: "Kuo Fakalahi 'a e Tohi!",
    viewHtmlDoc: 'Sio ki he Tohi HTML',
    downloadPdfDoc: 'Hilifaki ko ha PDF',
    generateAudioBtn: 'Fakatupu Ongo',
    stopAudioBtn: "Tamate'i 'a e Ongo",
    audioGenerating: "'Oku fakatupu 'a e ongo...",
    createPptxBtn: 'Fakatupu PowerPoint',
    presenterLabel: "Ho'o Hingoa (Kau Fakalea)",
    audienceLabel: "Taukei / Kakai 'e Ongoongo",
    pptxTitleLabel: "'Ulungaanga 'o e Fakamatala",
    generatePptxBtn: 'Fakatupu Fakamatala',
    pptxGenerating: "'Oku fakatupu PowerPoint...",
    pdfGenerating: "'Oku fakatupu PDF...",
    bibleVerseHint: "Move pe ala ki he ngaahi veesi kulokulo ke sio ki he Tohitapu Faka-Tonga KJV",
  }
} as const;

type Lang = 'en' | 'to';

const OFFICIAL_LANGUAGES = ['en', 'zh', 'es', 'hi', 'ar', 'bn', 'pt', 'ru', 'ja', 'id', 'fr', 'de', 'ko', 'it', 'nl'];
const UNOFFICIAL_LANGUAGES = ['to', 'mi', 'sm', 'fj', 'haw', 'ty', 'mh', 'ch', 'tpi', 'chk'];

const LANGUAGES = [
  { code: 'to', name: 'Tongan', flag: '🇹🇴' },
  { code: 'mi', name: 'Māori', flag: '🇳🇿' },
  { code: 'sm', name: 'Sāmoan', flag: '🇼🇸' },
  { code: 'fj', name: 'Fijian', flag: '🇫🇯' },
  { code: 'haw', name: 'Hawaiian', flag: '🇺🇸' },
  { code: 'ty', name: 'Tahitian', flag: '🇵🇫' },
  { code: 'mh', name: 'Marshallese', flag: '🇲🇭' },
  { code: 'ch', name: 'Chamorro', flag: '🇬🇺' },
  { code: 'tpi', name: 'Tok Pisin', flag: '🇵🇬' },
  { code: 'chk', name: 'Chuukese', flag: '🇫🇲' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'zh', name: 'Chinese (Simplified)', flag: '🇨🇳' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
  { code: 'bn', name: 'Bengali', flag: '🇧🇩' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
  { code: 'ru', name: 'Russian', flag: '🇷🇺' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'id', name: 'Indonesian', flag: '🇮🇩' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
];

interface Metrics {
  pages: number;
  chars: number;
  sessions: number;
  plan: string;
  remainingFast?: number;
  remainingPerfect?: number;
  cooldownUntil?: number;
  subscriptionEnd?: string;
  fastTranslations?: number;
  perfectTranslations?: number;
}

interface ResultData {
  filename?: string;
  charCount?: number;
  numPages?: number;
  downloadUrl: string;
  processingTime?: number;
  isPartial?: boolean;
}

interface DocumentQuality {
  isClean: boolean;
  warnings: string[];
  recommendations: string[];
  charCount: number;
  pageCount: number;
  estimatedTime: { fast: number; perfect: number };
}

interface Job {
  id: string;
  type: string;
  from_lang: string;
  to_lang: string;
  status: string;
  progress: number;
  output_file?: string;
  original_filename?: string;
  created_at: string;
  completed_at?: string;
  liveStatus?: string;
}

function TabPanel(props: { children?: React.ReactNode; value: number; index: number }) {
  const { children, value, index } = props;
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function PlanBadge({ plan }: { plan: string }) {
  const configs: Record<string, { color: 'default' | 'primary' | 'success' | 'warning'; icon: React.ReactNode }> = {
    free: { color: 'default', icon: null },
    premium: { color: 'primary', icon: <WorkspacePremium sx={{ fontSize: 14 }} /> },
    gold: { color: 'warning', icon: <WorkspacePremium sx={{ fontSize: 14 }} /> },
  };
  const cfg = configs[plan] || configs.free;
  return (
    <Chip
      label={plan.toUpperCase()}
      color={cfg.color}
      size="small"
      icon={cfg.icon as any}
      sx={{ fontWeight: 700 }}
    />
  );
}

const PREMIUM_MONTHLY_URL = 'https://tongamarket.com/shop/premium-translation-monthly/';
const PREMIUM_ANNUAL_URL = 'https://tongamarket.com/shop/premium-translation-annual/';
const GOLD_MONTHLY_URL = 'https://tongamarket.com/shop/gold-translation-monthly/';
const GOLD_ANNUAL_URL = 'https://tongamarket.com/shop/gold-translation-annual/';

export default function App() {
  const isMobile = useMediaQuery('(max-width:900px)');
  const [uiLang, setUiLang] = useState<Lang>('en');
  const T = UI[uiLang];

  const [tab, setTab] = useState(0);
  const [userId, setUserId] = useState('');
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  const [fromLang, setFromLang] = useState('en');
  const [toLang, setToLang] = useState('to');

  const isFromOfficial = OFFICIAL_LANGUAGES.includes(fromLang);
  const availableToLangs = isFromOfficial
    ? LANGUAGES.filter(l => UNOFFICIAL_LANGUAGES.includes(l.code))
    : LANGUAGES;

  useEffect(() => {
    if (isFromOfficial && !UNOFFICIAL_LANGUAGES.includes(toLang)) setToLang('to');
    else if (!isFromOfficial && toLang === fromLang) setToLang('en');
  }, [fromLang, toLang, isFromOfficial]);

  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ percent: number; msg?: string }>({ percent: 0 });
  const [result, setResult] = useState<ResultData | null>(null);
  const [error, setError] = useState<React.ReactNode | null>(null);
  const [quality, setQuality] = useState<DocumentQuality | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [pendingMode, setPendingMode] = useState<'fast' | 'perfect' | null>(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotDialog, setShowForgotDialog] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [directResetUrl, setDirectResetUrl] = useState('');

  // PWA Install state
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [showInstallBtn, setShowInstallBtn] = useState(false);

  const isPdf = false; // PDF support removed

  // ── Tab A: PDF → DOCX state ──────────────────────────────────────────────
  const [pdfFile, setPdfFile] = React.useState<File | null>(null);
  const [pdfConverting, setPdfConverting] = React.useState(false);
  const [pdfConvertResult, setPdfConvertResult] = React.useState<{downloadUrl:string;filename:string}|null>(null);
  const [pdfConvertError, setPdfConvertError] = React.useState('');

  // ── Tab B: Document Enhancer state ───────────────────────────────────────
  const [enhFile, setEnhFile] = React.useState<File | null>(null);
  const [enhancing, setEnhancing] = React.useState(false);
  const [enhJobId, setEnhJobId] = React.useState('');
  const [enhHtmlContent, setEnhHtmlContent] = React.useState('');
  const [enhHtmlUrl, setEnhHtmlUrl] = React.useState('');
  const [enhError, setEnhError] = React.useState('');
  const [audioGenerating, setAudioGenerating] = React.useState(false);
  const [audioSrc, setAudioSrc] = React.useState('');
  const [pdfDownloading, setPdfDownloading] = React.useState(false);
  const [pptxGenerating, setPptxGenerating] = React.useState(false);
  const [presenterName, setPresenterName] = React.useState('');
  const [audienceName, setAudienceName] = React.useState('');
  const [pptxDocTitle, setPptxDocTitle] = React.useState('');

  // Check URL for password reset token (from email link)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tok = params.get('reset_token');
    const em  = params.get('email');
    if (tok && em) {
      setForgotEmail(decodeURIComponent(em));
      setResetToken(tok);
      setShowForgotDialog(true);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);


  // PDF dropzone (Tab A)
  const { getRootProps: getPdfRootProps, getInputProps: getPdfInputProps, isDragActive: isPdfDragActive } = useDropzone({
    onDrop: (f) => { setPdfFile(f[0] || null); setPdfConvertResult(null); setPdfConvertError(''); },
    accept: { 'application/pdf': ['.pdf'] },
    maxSize: 50 * 1024 * 1024,
    onDropRejected: () => setPdfConvertError('Only PDF files accepted, max 50MB'),
  });

  // DOCX dropzone for Tab B (separate from main dropzone)
  const { getRootProps: getEnhRootProps, getInputProps: getEnhInputProps, isDragActive: isEnhDragActive } = useDropzone({
    onDrop: (f) => { setEnhFile(f[0] || null); setEnhJobId(''); setEnhHtmlContent(''); setEnhHtmlUrl(''); setEnhError(''); setAudioSrc(''); },
    accept: { 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] },
    maxSize: 50 * 1024 * 1024,
    onDropRejected: () => setEnhError('Only DOCX files accepted, max 50MB'),
  });

  const handlePdfConvert = async () => {
    if (!pdfFile) return;
    setPdfConverting(true); setPdfConvertError(''); setPdfConvertResult(null);
    const form = new FormData();
    form.append('file', pdfFile);
    if (userId) form.append('userId', userId);
    try {
      const res = await axios.post('/api/convert-pdf', form);
      setPdfConvertResult({ downloadUrl: res.data.downloadUrl, filename: res.data.filename });
    } catch (err: any) {
      setPdfConvertError(err.response?.data?.error || 'PDF conversion failed');
    } finally { setPdfConverting(false); }
  };

  const handleEnhanceDocument = async () => {
    if (!enhFile) return;
    setEnhancing(true); setEnhError(''); setEnhJobId(''); setEnhHtmlContent(''); setEnhHtmlUrl(''); setAudioSrc('');
    const form = new FormData();
    form.append('file', enhFile);
    if (userId) form.append('userId', userId);
    try {
      const res = await axios.post('/api/enhance-document', form);
      setEnhJobId(res.data.htmlJobId);
      setEnhHtmlContent(res.data.htmlContent);
      setEnhHtmlUrl(res.data.htmlFileUrl);
      // Auto-fill PPTX title from HTML content
      const titleMatch = res.data.htmlContent.match(/<title>([^<]+)<\/title>/i);
      if (titleMatch) setPptxDocTitle(titleMatch[1]);
    } catch (err: any) {
      setEnhError(err.response?.data?.error || 'Document enhancement failed');
    } finally { setEnhancing(false); }
  };

  const handleGenerateAudio = async () => {
    if (!enhHtmlContent) return;
    setAudioGenerating(true); setAudioSrc('');
    // Extract text from HTML
    const tmp = document.createElement('div');
    tmp.innerHTML = enhHtmlContent;
    const text = (tmp.textContent || tmp.innerText || '').slice(0, 4800);
    try {
      const res = await axios.post('/api/generate-audio', { text, lang: 'id' }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setAudioSrc(url);
    } catch (err: any) {
      setEnhError('Audio generation failed. Please try again.');
    } finally { setAudioGenerating(false); }
  };

  const handleDownloadPdf = async () => {
    if (!enhJobId) return;
    setPdfDownloading(true);
    try {
      const res = await axios.post('/api/html-to-pdf', { htmlJobId: enhJobId }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'document.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setEnhError('PDF generation failed: ' + (err.response?.data?.error || err.message));
    } finally { setPdfDownloading(false); }
  };

  const handleGeneratePptx = async () => {
    if (!enhJobId) return;
    setPptxGenerating(true);
    try {
      const res = await axios.post('/api/generate-pptx', {
        htmlJobId: enhJobId, presenter: presenterName, audience: audienceName, title: pptxDocTitle
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'presentation.pptx'; a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setEnhError('PPTX generation failed: ' + (err.response?.data?.error || err.message));
    } finally { setPptxGenerating(false); }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (f) => {
      setFile(f[0] || null);
      setQuality(null);
      setError('');
      setResult(null);
    },
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxSize: 50 * 1024 * 1024,
    onDropRejected: (fileRejections) => {
      const rejection = fileRejections[0];
      if (rejection.errors[0].code === 'file-invalid-type') {
        setError('Only DOCX files are supported. Please convert PDF to DOCX first.');
      } else if (rejection.errors[0].code === 'file-too-large') {
        setError('File too large. Maximum size is 50MB.');
      }
    }
  });

  useEffect(() => {
    if (userId) {
      setLoadingMetrics(true);
      axios.get(`/api/metrics/${userId}`).then(res => {
        setMetrics(res.data);
      }).catch(() => setMetrics(null)).finally(() => setLoadingMetrics(false));
    }
  }, [userId]);

  const fetchJobs = async (email?: string) => {
    const id = email || userId;
    if (!id) return;
    setLoadingJobs(true);
    try {
      const res = await axios.get('/api/jobs', {
        headers: { Authorization: `Bearer ${id}` },
      });
      setJobs(res.data.jobs || []);
    } catch {
      // silently ignore — jobs are a bonus feature
    } finally {
      setLoadingJobs(false);
    }
  };

  useEffect(() => {
    if (userId && tab === 2) fetchJobs();
  }, [userId, tab]);

  const redownload = async (jobId: string, filename?: string) => {
    try {
      const res = await axios.get(`/api/jobs/${jobId}/download`, {
        headers: { Authorization: `Bearer ${userId}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'translated.docx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Download failed. The file may no longer be available on the server.');
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem('userEmail');
    if (saved && saved.includes('@')) setUserId(saved);
    const savedLang = localStorage.getItem('uiLang') as Lang | null;
    if (savedLang === 'en' || savedLang === 'to') setUiLang(savedLang);
  }, []);

  // PWA install prompt
  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallBtn(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setShowInstallBtn(false);
    } else {
      setShowInstallDialog(true);
    }
  };

  const toggleUiLang = () => {
    const next: Lang = uiLang === 'en' ? 'to' : 'en';
    setUiLang(next);
    localStorage.setItem('uiLang', next);
  };

  const getSubscribeUrl = (baseUrl: string) => {
    if (!userId) return baseUrl;
    return `${baseUrl}?leatonga_email=${encodeURIComponent(userId)}`;
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const handleUpload = async (mode: 'fast' | 'perfect') => {
    if (!file || !userId) {
      setError(T.registerFirst);
      return;
    }

    setUploading(true);
    setError('');
    setResult(null);
    setProgress({ percent: 0 });

    if (isPdf) {
      setProgress({ percent: 5, msg: T.convertingPdf });
    }

    const form = new FormData();
    form.append('file', file);
    form.append('userId', userId);
    form.append('fromLang', fromLang);
    form.append('toLang', toLang);

    const endpoint = mode === 'fast' ? '/api/translate' : '/api/translate-perfect';

    try {
      const res = await axios.post(endpoint, form);

      if (res.data.requiresConfirmation) {
        setQuality(res.data.quality);
        setPendingMode(mode);
        setConfirmDialog(true);
        setUploading(false);
        return;
      }

      const jobId = res.data.jobId;
      setQuality(res.data.quality);

      const ws = new WebSocket(`wss://${window.location.host}/?jobId=${jobId}`);
      ws.onmessage = (e) => {
        const data = JSON.parse(e.data);
        setProgress({ percent: data.percent || 0, msg: data.msg });

        if (data.percent >= 100 && data.result) {
          setResult({
            downloadUrl: `/outputs/${data.result.filename}`,
            filename: data.result.filename,
            charCount: data.result.charCount,
            numPages: data.result.numPages,
            processingTime: data.result.processingTime,
            isPartial: data.result.isPartial,
          });
          ws.close();
          setUploading(false);
          axios.get(`/api/metrics/${userId}`).then(r => setMetrics(r.data));
          fetchJobs(userId);
        }
      };

      ws.onerror = () => {
        setError('Connection lost. Please try again.');
        setUploading(false);
      };

    } catch (err: any) {
      setUploading(false);
      if (err.response?.data?.exceedsLimit) {
        const q = err.response.data.quality;
        const planMax = metrics?.plan === 'gold' ? 100 : metrics?.plan === 'premium' ? 50 : 10;
        setError(
          <Box>
            <Typography color="error" variant="body1">
              Document too large for {metrics?.plan?.toUpperCase()} plan (max {planMax} pages). Your document: ~{q?.pageCount} pages.
            </Typography>
            <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" gap={1}>
              <Button variant="contained" href={getSubscribeUrl(GOLD_MONTHLY_URL)} target="_blank" size="small">
                Gold Monthly
              </Button>
              <Button variant="contained" color="success" href={getSubscribeUrl(GOLD_ANNUAL_URL)} target="_blank" size="small">
                Gold Annual (Save 33%)
              </Button>
              <Button variant="outlined" href="mailto:LeaTonga2026@gmail.com" size="small">
                Contact for large docs
              </Button>
            </Stack>
          </Box>
        );
        setQuality(q);
      } else {
        setError(err.response?.data?.error || 'Translation failed. Please try again.');
      }
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) { setForgotError('Please enter your email address'); return; }
    setForgotLoading(true); setForgotError(''); setDirectResetUrl('');
    try {
      const res = await axios.post('/api/forgot-password', { email: forgotEmail });
      if (res.data.directReset && res.data.resetUrl) {
        // SMTP not configured — server returned the URL directly
        setDirectResetUrl(res.data.resetUrl);
      }
      setForgotSent(true);
    } catch (err: any) {
      setForgotError(err.response?.data?.error || 'Failed to send reset email');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { setResetError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setResetError('Passwords do not match'); return; }
    setResetLoading(true); setResetError('');
    try {
      await axios.post('/api/reset-password', { email: forgotEmail, token: resetToken, newPassword });
      setResetSuccess(true);
    } catch (err: any) {
      setResetError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  const handleRegister = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput.trim())) {
      setEmailError('Please enter a valid email address');
      return;
    }
    if (!passwordInput || passwordInput.length < 6) {
      setRegisterError('Password must be at least 6 characters');
      return;
    }
    setRegisterLoading(true);
    setRegisterError('');
    setEmailError('');
    try {
      const email = emailInput.trim().toLowerCase();
      const res = await axios.post('/api/register', { email, password: passwordInput });
      if (!res.data.success) throw new Error(res.data.error || 'Authentication failed');
      setUserId(email);
      localStorage.setItem('userEmail', email);
      const metricsRes = await axios.get(`/api/metrics/${email}`);
      setMetrics(metricsRes.data);
      setEmailInput('');
      setPasswordInput('');
      setTab(0);
    } catch (err: any) {
      setRegisterError(err.response?.data?.error || err.message || 'Authentication failed. Try again.');
    } finally {
      setRegisterLoading(false);
    }
  };

  const download = (url: string, name?: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = name || 'translated.docx';
    a.click();
  };

  const DropZoneContent = () => (
    <Box
      {...getRootProps()}
      sx={{
        border: `2px dashed`,
        borderColor: isDragActive ? 'primary.main' : 'rgba(200,16,46,0.3)',
        borderRadius: 3,
        p: { xs: 4, md: 6 },
        textAlign: 'center',
        cursor: 'pointer',
        bgcolor: isDragActive ? 'rgba(200,16,46,0.04)' : '#FAFAFA',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: 'rgba(200,16,46,0.03)',
        },
      }}
    >
      <input {...getInputProps()} />
      {file ? (
        <Stack spacing={1} alignItems="center">
          {isPdf
            ? <PictureAsPdf sx={{ fontSize: 56, color: '#E53935' }} />
            : <Description sx={{ fontSize: 56, color: 'primary.main' }} />}
          <Typography variant="h6" fontWeight={600}>{file.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {(file.size / 1024 / 1024).toFixed(2)} MB
            {isPdf && (
              <Chip label="PDF → will auto-convert" size="small" color="warning" sx={{ ml: 1 }} />
            )}
          </Typography>
          <Button size="small" color="inherit" onClick={(e) => { e.stopPropagation(); setFile(null); setQuality(null); }}>
            {T.removeFile}
          </Button>
        </Stack>
      ) : (
        <Stack spacing={1} alignItems="center">
          <CloudUpload sx={{ fontSize: 56, color: isDragActive ? 'primary.main' : 'text.secondary' }} />
          <Typography variant="h6" fontWeight={600} color={isDragActive ? 'primary.main' : 'text.primary'}>
            {isDragActive ? T.dropActive : T.dropZone}
          </Typography>
          <Typography variant="body2" color="text.secondary">{T.dropZoneClick}</Typography>
          <Stack direction="row" spacing={1} mt={1}>
            <Chip icon={<Description sx={{ fontSize: 14 }} />} label="DOCX" size="small" variant="outlined" color="primary" />
          </Stack>
          <Typography variant="caption" color="text.secondary" align="center" sx={{ maxWidth: 480, lineHeight: 1.6 }}>{T.fileTypes}</Typography>
        </Stack>
      )}
    </Box>
  );

  const QualityInfo = ({ mode }: { mode: 'fast' | 'perfect' }) => {
    if (!quality) return null;
    const estTime = mode === 'fast' ? quality.estimatedTime.fast : quality.estimatedTime.perfect;
    return (
      <Alert
        severity={quality.isClean ? 'success' : 'warning'}
        sx={{ mt: 2, borderRadius: 2 }}
        icon={quality.isClean ? <CheckCircle /> : <Warning />}
      >
        <Stack spacing={0.5}>
          <Typography variant="subtitle2" fontWeight={700}>{T.docAssessment}</Typography>
          <Typography variant="body2">
            📄 {quality.charCount.toLocaleString()} {T.chars} · ~{quality.pageCount} {T.pages}
          </Typography>
          <Typography variant="body2">
            ⏱ {T.estTime}: {formatTime(estTime)}
          </Typography>
          {quality.warnings.map((w, i) => <Typography key={i} variant="body2">⚠️ {w}</Typography>)}
          {quality.recommendations.map((r, i) => <Typography key={i} variant="body2">💡 {r}</Typography>)}
        </Stack>
      </Alert>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Helmet>
        <title>LeaTonga - Translate Tongan, Māori, Sāmoan & More</title>
        <meta name="description" content="Professional DOCX & PDF document translation for Pacific languages." />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      </Helmet>

      {/* ── HEADER ── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1A2744 0%, #C8102E 100%)',
          color: 'white',
          py: { xs: 2, md: 3 },
          px: { xs: 2, md: 4 },
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 2px 20px rgba(0,0,0,0.2)',
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {/* Logo */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                }}
              >
                🇹🇴
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.1, letterSpacing: '-0.5px' }}>
                  LeaTonga
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, display: { xs: 'none', sm: 'block' } }}>
                  {T.tagline}
                </Typography>
              </Box>
            </Stack>

            {/* Right controls */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={T.langToggle}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption" sx={{ color: uiLang === 'en' ? 'white' : 'rgba(255,255,255,0.5)', fontWeight: 700 }}>
                    EN
                  </Typography>
                  <Switch
                    checked={uiLang === 'to'}
                    onChange={toggleUiLang}
                    size="small"
                    sx={{
                      '& .MuiSwitch-thumb': { bgcolor: 'white' },
                      '& .MuiSwitch-track': { bgcolor: 'rgba(255,255,255,0.4)' },
                    }}
                  />
                  <Typography variant="caption" sx={{ color: uiLang === 'to' ? 'white' : 'rgba(255,255,255,0.5)', fontWeight: 700 }}>
                    TO
                  </Typography>
                </Stack>
              </Tooltip>
              {userId && (
                <Tooltip title={userId}>
                  <Avatar
                    sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 14, cursor: 'pointer' }}
                    onClick={() => setTab(2)}
                  >
                    {userId[0].toUpperCase()}
                  </Avatar>
                </Tooltip>
              )}
              <Tooltip title={uiLang === 'en' ? 'Install App' : "'Inisitolo e Polokalama'"}>
                <IconButton
                  onClick={handleInstallClick}
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    p: 0.5,
                    border: '1px solid rgba(255,255,255,0.25)',
                    borderRadius: 1.5,
                    gap: 0.5,
                    px: 1,
                  }}
                >
                  <Download sx={{ fontSize: 16 }} />
                  <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 700, display: { xs: 'none', sm: 'block' } }}>
                    {uiLang === 'en' ? 'Install' : 'Tauhi'}
                  </Typography>
                </IconButton>
              </Tooltip>
              <Tooltip title="Admin">
                <IconButton onClick={() => setShowAdmin(true)} sx={{ color: 'rgba(255,255,255,0.7)', p: 0.5 }}>
                  <AdminPanelSettings fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* ── MAIN CONTENT ── */}
      <Box sx={{ bgcolor: 'background.default', minHeight: 'calc(100vh - 80px)' }}>
        <Container maxWidth="lg" sx={{ py: 3 }}>

          {/* ── REGISTRATION / ACCOUNT STATUS ── */}
          {!userId ? (
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                    <Person />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>{T.yourAccount}</Typography>
                    <Typography variant="body2" color="text.secondary">{T.accountNote}</Typography>
                  </Box>
                </Stack>

                <Box sx={{ maxWidth: 480 }}>
                  <TextField
                    fullWidth
                    label={T.emailLabel}
                    type="email"
                    value={emailInput}
                    onChange={(e) => { setEmailInput(e.target.value); setEmailError(''); setRegisterError(''); }}
                    error={!!emailError}
                    helperText={emailError || ' '}
                    placeholder={T.emailPlaceholder}
                    autoComplete="email"
                    onKeyDown={(e) => e.key === 'Enter' && handleRegister()}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    label="Password (min. 6 characters)"
                    type={showPassword ? 'text' : 'password'}
                    value={passwordInput}
                    onChange={(e) => { setPasswordInput(e.target.value); setRegisterError(''); }}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    onKeyDown={(e) => e.key === 'Enter' && handleRegister()}
                    sx={{ mb: 0.5 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small" tabIndex={-1}>
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  <Box sx={{ textAlign: 'right', mb: 1.5 }}>
                    <Button
                      size="small"
                      onClick={() => { setForgotEmail(emailInput); setShowForgotDialog(true); setForgotSent(false); setForgotError(''); setResetToken(''); setNewPassword(''); setConfirmPassword(''); setResetSuccess(false); setResetError(''); }}
                      sx={{ textTransform: 'none', fontSize: '0.8rem', color: 'text.secondary' }}
                    >
                      Forgot password?
                    </Button>
                  </Box>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleRegister}
                    disabled={registerLoading}
                    startIcon={registerLoading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : null}
                  >
                    {registerLoading ? T.registering : 'Register / Sign In'}
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    New users: creates your account. Returning users: signs you in.
                  </Typography>
                  {registerError && <Alert severity="error" sx={{ mt: 2 }}>{registerError}</Alert>}
                </Box>
              </CardContent>
            </Card>
          ) : (
            metrics && (
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #1A2744 0%, #2D3F6E 100%)',
                  borderRadius: 3,
                  p: 2,
                  mb: 3,
                  color: 'white',
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={1}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 36, height: 36 }}>
                      {userId[0].toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>{userId}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PlanBadge plan={metrics.plan} />
                        {metrics.subscriptionEnd && (
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {T.renewsOn} {new Date(metrics.subscriptionEnd).toLocaleDateString()}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                  {metrics.plan === 'free' && (
                    <Stack direction="row" spacing={2}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={700}>{metrics.remainingFast ?? 0}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>{T.fastRemaining}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={700}>{metrics.remainingPerfect ?? 0}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>{T.perfectRemaining}</Typography>
                      </Box>
                    </Stack>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => { setUserId(''); setMetrics(null); setJobs([]); localStorage.removeItem('userEmail'); }}
                    sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'white', color: 'white' }, minWidth: 80 }}
                  >
                    Logout
                  </Button>
                </Stack>
              </Box>
            )
          )}

          {/* ── LANGUAGE SELECTORS ── */}
          {(tab === 0 || tab === 1) && userId && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
              <FormControl fullWidth>
                <InputLabel>{T.fromLang}</InputLabel>
                <Select
                  value={fromLang}
                  label={T.fromLang}
                  onChange={(e) => setFromLang(e.target.value as string)}
                >
                  {LANGUAGES.map(l => (
                    <MenuItem key={l.code} value={l.code}>{l.flag} {l.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Translate sx={{ color: 'text.secondary', fontSize: 28 }} />
              </Box>
              <FormControl fullWidth>
                <InputLabel>{T.toLang}</InputLabel>
                <Select
                  value={toLang}
                  label={T.toLang}
                  onChange={(e) => setToLang(e.target.value as string)}
                >
                  {availableToLangs.filter(l => l.code !== fromLang).map(l => (
                    <MenuItem key={l.code} value={l.code}>{l.flag} {l.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          )}

          {/* ── TABS ── */}
          <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', mb: 2, border: '1px solid rgba(0,0,0,0.08)' }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant={isMobile ? 'scrollable' : 'fullWidth'}
              centered={!isMobile}
              sx={{
                '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
                bgcolor: 'background.paper',
              }}
            >
              <Tab icon={<Speed fontSize="small" />} iconPosition="start" label={T.fastTab} />
              <Tab icon={<AutoAwesome fontSize="small" />} iconPosition="start" label={T.perfectTab} />
              <Tab icon={<Person fontSize="small" />} iconPosition="start" label={T.metricsTab} />
              <Tab icon={<WorkspacePremium fontSize="small" />} iconPosition="start" label={T.subscribeTab} />
              <Tab icon={<Transform fontSize="small" />} iconPosition="start" label={T.pdfTab} />
              <Tab icon={<Article fontSize="small" />} iconPosition="start" label={T.enhanceTab} />
            </Tabs>
          </Paper>

          {/* ── FAST TRANSLATION TAB ── */}
          <TabPanel value={tab} index={0}>
            <Card>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Speed color="primary" />
                  <Typography variant="h6" fontWeight={700}>{T.fastTitle}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" mb={2}>{T.fastNote}</Typography>

                {!userId ? (
                  <Alert severity="info">{T.registerFirst}</Alert>
                ) : (
                  <>
                    {DropZoneContent()}
                    <QualityInfo mode="fast" />
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      sx={{ mt: 2 }}
                      onClick={() => handleUpload('fast')}
                      disabled={!file || uploading}
                      startIcon={uploading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Speed />}
                    >
                      {uploading ? T.uploading : T.startFast}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabPanel>

          {/* ── PERFECT FORMATTING TAB ── */}
          <TabPanel value={tab} index={1}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F0FF 100%)',
                border: '1px solid rgba(200,16,46,0.15)',
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                  <AutoAwesome sx={{ color: '#C8102E' }} />
                  <Typography variant="h6" fontWeight={700}>{T.perfectTitle}</Typography>
                  <Chip label={T.recommended} color="primary" size="small" sx={{ ml: 1 }} />
                </Stack>
                <Typography variant="body2" color="text.secondary" mb={2}>{T.perfectNote}</Typography>

                {!userId ? (
                  <Alert severity="info">{T.registerFirst}</Alert>
                ) : (
                  <>
                    {DropZoneContent()}
                    <QualityInfo mode="perfect" />
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      sx={{ mt: 2 }}
                      onClick={() => handleUpload('perfect')}
                      disabled={!file || uploading}
                      startIcon={uploading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <AutoAwesome />}
                    >
                      {uploading ? T.uploading : T.startPerfect}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabPanel>

          {/* ── METRICS TAB ── */}
          <TabPanel value={tab} index={2}>
            <Card>
              <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                <Typography variant="h5" fontWeight={700} mb={3}>{T.metricsTitle}</Typography>
                {loadingMetrics ? (
                  <Stack alignItems="center" py={4}><CircularProgress /></Stack>
                ) : metrics ? (
                  <Stack spacing={2}>
                    <Paper sx={{ p: 2, bgcolor: '#F8F9FA', borderRadius: 2 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Email</Typography>
                        <Typography fontWeight={600}>{userId}</Typography>
                      </Stack>
                      <Divider sx={{ my: 1.5 }} />
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">{T.plan}</Typography>
                        <PlanBadge plan={metrics.plan} />
                      </Stack>
                      {metrics.subscriptionEnd && (
                        <>
                          <Divider sx={{ my: 1.5 }} />
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">{T.renewsOn}</Typography>
                            <Typography fontWeight={600}>{new Date(metrics.subscriptionEnd).toLocaleDateString()}</Typography>
                          </Stack>
                        </>
                      )}
                    </Paper>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr 1fr' },
                        gap: 2,
                      }}
                    >
                      {[
                        { label: T.totalSessions, value: metrics.sessions },
                        { label: T.totalChars, value: (metrics.chars || 0).toLocaleString() },
                        { label: T.totalPages, value: metrics.pages },
                        { label: T.fastDone, value: metrics.fastTranslations || 0 },
                        { label: T.perfectDone, value: metrics.perfectTranslations || 0 },
                      ].map((stat, i) => (
                        <Paper key={i} sx={{ p: 2, textAlign: 'center', bgcolor: '#F8F9FA', borderRadius: 2 }}>
                          <Typography variant="h5" fontWeight={800} color="primary.main">{stat.value}</Typography>
                          <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                        </Paper>
                      ))}
                    </Box>
                  </Stack>
                ) : (
                  <Alert severity="info">{T.noMetrics}</Alert>
                )}

                {/* ── RECENT TRANSLATIONS ── */}
                {userId && (
                  <Box sx={{ mt: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <History sx={{ color: 'text.secondary', fontSize: 20 }} />
                        <Typography variant="h6" fontWeight={700}>{T.recentTranslations}</Typography>
                      </Stack>
                      <Tooltip title={T.refreshJobs}>
                        <IconButton size="small" onClick={() => fetchJobs()} disabled={loadingJobs}>
                          {loadingJobs ? <CircularProgress size={16} /> : <Refresh fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    {loadingJobs && jobs.length === 0 ? (
                      <Stack alignItems="center" py={3}><CircularProgress size={24} /></Stack>
                    ) : jobs.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        {T.noRecentJobs}
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        {jobs.map((job) => {
                          const fromFlag = LANGUAGES.find(l => l.code === job.from_lang)?.flag || '';
                          const toFlag = LANGUAGES.find(l => l.code === job.to_lang)?.flag || '';
                          const fromName = LANGUAGES.find(l => l.code === job.from_lang)?.name || job.from_lang;
                          const toName = LANGUAGES.find(l => l.code === job.to_lang)?.name || job.to_lang;
                          const isCompleted = job.status === 'completed';
                          const isProcessing = job.status === 'processing';
                          const isFailed = job.status === 'failed';
                          const jobDate = new Date(job.created_at).toLocaleString();
                          const typeLabel = job.type === 'perfect' ? '✨ Perfect' : '⚡ Fast';

                          return (
                            <Paper
                              key={job.id}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: isCompleted ? 'success.light' : isFailed ? 'error.light' : 'divider',
                                bgcolor: isCompleted ? '#F1F9F4' : isFailed ? '#FFF5F5' : '#FAFAFA',
                              }}
                            >
                              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={0.5}>
                                    <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: 220 }}>
                                      {job.original_filename || job.id.slice(0, 12) + '…'}
                                    </Typography>
                                    <Chip
                                      label={typeLabel}
                                      size="small"
                                      color={job.type === 'perfect' ? 'secondary' : 'primary'}
                                      variant="outlined"
                                      sx={{ fontSize: 11 }}
                                    />
                                    <Chip
                                      label={isCompleted ? T.jobCompleted : isProcessing ? T.jobProcessing : T.jobFailed}
                                      size="small"
                                      color={isCompleted ? 'success' : isFailed ? 'error' : 'warning'}
                                    />
                                  </Stack>
                                  <Typography variant="caption" color="text.secondary">
                                    {fromFlag} {fromName} → {toFlag} {toName} · {jobDate}
                                  </Typography>
                                  {isProcessing && (
                                    <Box sx={{ mt: 1 }}>
                                      <LinearProgress
                                        variant={job.progress > 0 ? 'determinate' : 'indeterminate'}
                                        value={job.progress}
                                        sx={{ height: 4, borderRadius: 2 }}
                                      />
                                      <Typography variant="caption" color="text.secondary">
                                        {job.liveStatus || T.processing} {job.progress > 0 ? `· ${job.progress}%` : ''}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                                {isCompleted && job.output_file && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    startIcon={<Download sx={{ fontSize: 16 }} />}
                                    onClick={() => redownload(job.id, job.original_filename)}
                                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                  >
                                    {T.redownload}
                                  </Button>
                                )}
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </TabPanel>

          {/* ── SUBSCRIBE TAB ── */}
          <TabPanel value={tab} index={3}>
            <Card>
              <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                <Typography variant="h5" fontWeight={700} mb={2}>{T.subscriptionPlans}</Typography>

                {!userId ? (
                  <Alert severity="warning" sx={{ mb: 3 }}>{T.loginPrompt}</Alert>
                ) : (
                  <Alert severity="info" sx={{ mb: 3 }}>{T.prefilledNote}</Alert>
                )}

                <Stack spacing={4}>
                  {/* FREE */}
                  <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                    <Typography variant="h6" fontWeight={700}>{T.free}</Typography>
                    <Typography variant="h3" fontWeight={800} color="text.secondary" my={1}>$0</Typography>
                    <Divider sx={{ my: 2 }} />
                    <Stack spacing={0.5} textAlign="left" sx={{ maxWidth: 280, mx: 'auto' }}>
                      <Typography>✓ Up to 30,000 {T.perCharLimit}</Typography>
                      <Typography>✓ Up to 10 {T.perPageLimit}</Typography>
                      <Typography>✓ Basic translation limits</Typography>
                      <Typography color="text.secondary" variant="caption" sx={{ mt: 1 }}>
                        {T.upgradeForFull}
                      </Typography>
                    </Stack>
                    <Button variant="outlined" fullWidth sx={{ mt: 3 }} disabled>{T.currentPlan}</Button>
                  </Paper>

                  {/* PREMIUM */}
                  <Box>
                    <Typography variant="h6" fontWeight={700} mb={2}>Premium</Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <Paper sx={{ flex: 1, p: 3, textAlign: 'center', borderRadius: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600}>{T.monthly}</Typography>
                        <Typography variant="h4" fontWeight={800} my={1}>$22.50<Typography component="span" variant="body1">/mo</Typography></Typography>
                        <Button variant="contained" fullWidth sx={{ mt: 2 }} href={getSubscribeUrl(PREMIUM_MONTHLY_URL)} target="_blank" disabled={!userId}>
                          {T.subscribeBtnMonthly}
                        </Button>
                      </Paper>
                      <Paper
                        sx={{
                          flex: 1, p: 3, textAlign: 'center', borderRadius: 3,
                          border: '2px solid', borderColor: 'primary.main',
                          position: 'relative',
                        }}
                      >
                        <Chip label={T.popular} color="primary" size="small" sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)' }} />
                        <Typography variant="subtitle1" fontWeight={600}>{T.annualLabel}</Typography>
                        <Typography variant="h4" fontWeight={800} my={0.5}>$180<Typography component="span" variant="body1">/yr</Typography></Typography>
                        <Typography color="success.main" variant="body2" fontWeight={600}>{T.annualSave} · $15/mo</Typography>
                        <Button variant="contained" color="primary" fullWidth sx={{ mt: 2 }} href={getSubscribeUrl(PREMIUM_ANNUAL_URL)} target="_blank" disabled={!userId}>
                          {T.subscribeBtnAnnual}
                        </Button>
                      </Paper>
                    </Stack>
                    <Stack spacing={0.5} sx={{ mt: 2, pl: 1 }}>
                      <Typography fontWeight={700} variant="body2">{T.premiumIncludes}</Typography>
                      <Typography variant="body2">✓ Up to 150,000 {T.perCharLimit}</Typography>
                      <Typography variant="body2">✓ Up to 50 {T.perPageLimit}</Typography>
                      <Typography variant="body2">✓ Higher daily translation limits</Typography>
                      <Typography variant="body2">✓ Partial previews for oversized documents</Typography>
                    </Stack>
                  </Box>

                  {/* GOLD */}
                  <Box>
                    <Typography variant="h6" fontWeight={700} mb={2}>Gold</Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <Paper sx={{ flex: 1, p: 3, textAlign: 'center', borderRadius: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600}>{T.monthly}</Typography>
                        <Typography variant="h4" fontWeight={800} my={1}>$45<Typography component="span" variant="body1">/mo</Typography></Typography>
                        <Button variant="contained" color="success" fullWidth sx={{ mt: 2 }} href={getSubscribeUrl(GOLD_MONTHLY_URL)} target="_blank" disabled={!userId}>
                          {T.subscribeBtnMonthly}
                        </Button>
                      </Paper>
                      <Paper
                        sx={{
                          flex: 1, p: 3, textAlign: 'center', borderRadius: 3,
                          bgcolor: '#F1F9F4',
                          border: '2px solid', borderColor: 'success.main',
                          position: 'relative',
                        }}
                      >
                        <Chip label={T.bestValue} color="success" size="small" sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)' }} />
                        <Typography variant="subtitle1" fontWeight={600}>{T.annualLabel}</Typography>
                        <Typography variant="h4" fontWeight={800} my={0.5}>$360<Typography component="span" variant="body1">/yr</Typography></Typography>
                        <Typography color="success.main" variant="body2" fontWeight={600}>{T.annualSave} · $30/mo</Typography>
                        <Button variant="contained" color="success" fullWidth sx={{ mt: 2 }} href={getSubscribeUrl(GOLD_ANNUAL_URL)} target="_blank" disabled={!userId}>
                          {T.subscribeBtnAnnual}
                        </Button>
                      </Paper>
                    </Stack>
                    <Stack spacing={0.5} sx={{ mt: 2, pl: 1 }}>
                      <Typography fontWeight={700} variant="body2">{T.goldIncludes}</Typography>
                      <Typography variant="body2">✓ Up to 300,000 {T.perCharLimit}</Typography>
                      <Typography variant="body2">✓ Up to 100 {T.perPageLimit}</Typography>
                      <Typography variant="body2">✓ Unlimited daily translations</Typography>
                      <Typography variant="body2">✓ Partial previews for oversized documents</Typography>
                    </Stack>
                  </Box>
                </Stack>

                <Divider sx={{ my: 3 }} />
                <Alert severity="info" sx={{ mb: 2 }}>{T.subscriptionNote}</Alert>
                <Typography variant="body2" align="center" color="text.secondary">
                  {T.contactLarge}{' '}
                  <a href="mailto:LeaTonga2026@gmail.com" style={{ color: '#C8102E', fontWeight: 600 }}>
                    LeaTonga2026@gmail.com
                  </a>
                </Typography>
              </CardContent>
            </Card>
          </TabPanel>

          {/* ── TAB A: PDF → DOCX ── */}
          <TabPanel value={tab} index={4}>
            <Card sx={{ border: '1px solid rgba(200,16,46,0.15)' }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Transform color="primary" />
                  <Typography variant="h6" fontWeight={700}>{T.pdfTitle}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" mb={2}>{T.pdfDesc}</Typography>

                {/* PDF dropzone */}
                <Box
                  {...getPdfRootProps()}
                  sx={{
                    border: `2px dashed`, borderColor: isPdfDragActive ? 'primary.main' : 'rgba(200,16,46,0.3)',
                    borderRadius: 3, p: { xs: 4, md: 6 }, textAlign: 'center', cursor: 'pointer',
                    bgcolor: isPdfDragActive ? 'rgba(200,16,46,0.04)' : '#FAFAFA',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(200,16,46,0.03)' },
                  }}
                >
                  <input {...getPdfInputProps()} />
                  {pdfFile ? (
                    <Stack spacing={1} alignItems="center">
                      <PictureAsPdf sx={{ fontSize: 56, color: '#E53935' }} />
                      <Typography variant="h6" fontWeight={600}>{pdfFile.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{(pdfFile.size/1024/1024).toFixed(2)} MB</Typography>
                      <Button size="small" color="inherit" onClick={e => { e.stopPropagation(); setPdfFile(null); setPdfConvertResult(null); }}>
                        {T.removeFile}
                      </Button>
                    </Stack>
                  ) : (
                    <Stack spacing={1} alignItems="center">
                      <PictureAsPdf sx={{ fontSize: 56, color: isPdfDragActive ? 'primary.main' : 'text.secondary' }} />
                      <Typography variant="h6" fontWeight={600} color={isPdfDragActive ? 'primary.main' : 'text.primary'}>
                        {isPdfDragActive ? T.dropActive : T.pdfDropZone}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{T.dropZoneClick}</Typography>
                      <Chip icon={<PictureAsPdf sx={{ fontSize: 14 }} />} label="PDF" size="small" variant="outlined" color="error" />
                      <Typography variant="caption" color="text.secondary">{T.pdfFileTypes}</Typography>
                    </Stack>
                  )}
                </Box>

                {pdfConvertError && <Alert severity="error" sx={{ mt: 2 }}>{pdfConvertError}</Alert>}

                <Button
                  fullWidth variant="contained" size="large" sx={{ mt: 2 }}
                  onClick={handlePdfConvert}
                  disabled={!pdfFile || pdfConverting}
                  startIcon={pdfConverting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Transform />}
                >
                  {pdfConverting ? T.pdfConvertProgress : T.pdfConvertBtn}
                </Button>

                {pdfConvertResult && (
                  <Card sx={{ mt: 3, background: 'linear-gradient(135deg,#F1F9F4,#E8F5E9)', border: '2px solid #1B7340' }}>
                    <CardContent>
                      <Stack alignItems="center" spacing={1.5}>
                        <CheckCircle sx={{ color: '#1B7340', fontSize: 40 }} />
                        <Typography variant="h6" fontWeight={700} color="success.dark">{T.pdfDone}</Typography>
                        <Typography variant="body2" color="text.secondary">{pdfConvertResult.filename}</Typography>
                        <Button
                          fullWidth variant="contained" color="success" size="large"
                          href={pdfConvertResult.downloadUrl} download={pdfConvertResult.filename}
                          startIcon={<Download />}
                        >
                          {T.pdfDownload}
                        </Button>
                        <Alert severity="info" sx={{ width: '100%' }}>
                          {T.pdfNextStep}
                        </Alert>
                      </Stack>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          </TabPanel>

          {/* ── TAB B: DOCUMENT ENHANCER ── */}
          <TabPanel value={tab} index={5}>
            <Card sx={{ border: '1px solid rgba(26,39,68,0.15)' }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Article sx={{ color: '#1A2744' }} />
                  <Typography variant="h6" fontWeight={700}>{T.enhanceTitle}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" mb={2}>{T.enhanceDesc}</Typography>

                {/* DOCX dropzone */}
                <Box
                  {...getEnhRootProps()}
                  sx={{
                    border: `2px dashed`, borderColor: isEnhDragActive ? '#1A2744' : 'rgba(26,39,68,0.3)',
                    borderRadius: 3, p: { xs: 3, md: 5 }, textAlign: 'center', cursor: 'pointer',
                    bgcolor: isEnhDragActive ? 'rgba(26,39,68,0.04)' : '#FAFAFA',
                    '&:hover': { borderColor: '#1A2744', bgcolor: 'rgba(26,39,68,0.03)' },
                  }}
                >
                  <input {...getEnhInputProps()} />
                  {enhFile ? (
                    <Stack spacing={1} alignItems="center">
                      <Description sx={{ fontSize: 48, color: '#1A2744' }} />
                      <Typography variant="h6" fontWeight={600}>{enhFile.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{(enhFile.size/1024/1024).toFixed(2)} MB</Typography>
                      <Button size="small" color="inherit" onClick={e => { e.stopPropagation(); setEnhFile(null); setEnhHtmlContent(''); setEnhJobId(''); }}>
                        {T.removeFile}
                      </Button>
                    </Stack>
                  ) : (
                    <Stack spacing={1} alignItems="center">
                      <Description sx={{ fontSize: 48, color: isEnhDragActive ? '#1A2744' : 'text.secondary' }} />
                      <Typography variant="h6" fontWeight={600} color={isEnhDragActive ? '#1A2744' : 'text.primary'}>
                        {isEnhDragActive ? T.dropActive : T.enhanceDropZone}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{T.dropZoneClick}</Typography>
                      <Chip icon={<Description sx={{ fontSize: 14 }} />} label="DOCX" size="small" variant="outlined" sx={{ borderColor: '#1A2744' }} />
                    </Stack>
                  )}
                </Box>

                {enhError && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setEnhError('')}>{enhError}</Alert>}

                <Button
                  fullWidth variant="contained" size="large"
                  sx={{ mt: 2, bgcolor: '#1A2744', '&:hover': { bgcolor: '#2d3f6e' } }}
                  onClick={handleEnhanceDocument}
                  disabled={!enhFile || enhancing}
                  startIcon={enhancing ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Article />}
                >
                  {enhancing ? T.enhancing : T.enhanceConvertBtn}
                </Button>

                {/* ── ENHANCED OUTPUT ── */}
                {enhHtmlContent && (
                  <Stack spacing={2} sx={{ mt: 3 }}>
                    <Alert severity="success" icon={<CheckCircle />}>
                      {T.enhanceDone}
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.8 }}>
                        {T.bibleVerseHint}
                      </Typography>
                    </Alert>

                    {/* HTML Preview */}
                    <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.1)' }}>
                      <Box sx={{ p: 1.5, bgcolor: '#1A2744', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="white" fontWeight={700}>HTML Preview</Typography>
                        <Button
                          size="small" variant="outlined"
                          sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                          href={enhHtmlUrl} target="_blank"
                          startIcon={<OpenInNew fontSize="small" />}
                        >
                          {T.viewHtmlDoc}
                        </Button>
                      </Box>
                      <Box sx={{ height: 480, overflow: 'hidden' }}>
                        <iframe
                          srcDoc={enhHtmlContent}
                          style={{ width: '100%', height: '100%', border: 'none' }}
                          title="Enhanced Document Preview"
                          sandbox="allow-scripts allow-same-origin"
                        />
                      </Box>
                    </Paper>

                    {/* Action Buttons Row */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      {/* Audio */}
                      <Paper sx={{ flex: 1, p: 2, borderRadius: 2, border: '1px solid rgba(0,0,0,0.1)' }}>
                        <Stack spacing={1.5}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <VolumeUp color="primary" />
                            <Typography variant="subtitle2" fontWeight={700}>Tongan Audio</Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">Indonesian phonetic reading of Tongan script</Typography>
                          {audioSrc ? (
                            <Stack spacing={1}>
                              <audio controls src={audioSrc} style={{ width: '100%' }} autoPlay />
                              <Button size="small" variant="outlined" color="error" onClick={() => setAudioSrc('')} startIcon={<StopCircle />}>
                                {T.stopAudioBtn}
                              </Button>
                            </Stack>
                          ) : (
                            <Button
                              variant="contained" onClick={handleGenerateAudio}
                              disabled={audioGenerating}
                              startIcon={audioGenerating ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <VolumeUp />}
                            >
                              {audioGenerating ? T.audioGenerating : T.generateAudioBtn}
                            </Button>
                          )}
                        </Stack>
                      </Paper>

                      {/* PDF Download */}
                      <Paper sx={{ flex: 1, p: 2, borderRadius: 2, border: '1px solid rgba(0,0,0,0.1)' }}>
                        <Stack spacing={1.5}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <PictureAsPdf color="error" />
                            <Typography variant="subtitle2" fontWeight={700}>PDF Export</Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">Professional A4 PDF with all formatting preserved</Typography>
                          <Button
                            variant="contained" color="error" onClick={handleDownloadPdf}
                            disabled={pdfDownloading}
                            startIcon={pdfDownloading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <Download />}
                          >
                            {pdfDownloading ? T.pdfGenerating : T.downloadPdfDoc}
                          </Button>
                        </Stack>
                      </Paper>
                    </Stack>

                    {/* PowerPoint Creator */}
                    <Paper sx={{ p: 2.5, borderRadius: 2, border: '2px solid #1A2744', background: 'linear-gradient(135deg,#F8F9FF,#EEF0FF)' }}>
                      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                        <Slideshow sx={{ color: '#1A2744' }} />
                        <Typography variant="subtitle1" fontWeight={700} color="#1A2744">{T.createPptxBtn}</Typography>
                        <Chip label="PPTX" size="small" sx={{ bgcolor: '#1A2744', color: 'white', fontSize: 10 }} />
                      </Stack>
                      <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <TextField
                            fullWidth label={T.presenterLabel} size="small"
                            value={presenterName} onChange={e => setPresenterName(e.target.value)}
                            placeholder="e.g. Sione Taufa"
                          />
                          <TextField
                            fullWidth label={T.audienceLabel} size="small"
                            value={audienceName} onChange={e => setAudienceName(e.target.value)}
                            placeholder="e.g. Church Leaders"
                          />
                        </Stack>
                        <TextField
                          fullWidth label={T.pptxTitleLabel} size="small"
                          value={pptxDocTitle} onChange={e => setPptxDocTitle(e.target.value)}
                        />
                        <Button
                          variant="contained" size="large"
                          sx={{ bgcolor: '#1A2744', '&:hover': { bgcolor: '#2d3f6e' } }}
                          onClick={handleGeneratePptx}
                          disabled={pptxGenerating || !enhJobId}
                          startIcon={pptxGenerating ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Slideshow />}
                        >
                          {pptxGenerating ? T.pptxGenerating : T.generatePptxBtn}
                        </Button>
                      </Stack>
                    </Paper>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </TabPanel>

          {/* ── PROGRESS BAR ── */}
          {uploading && progress.percent > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  {progress.msg || T.processing}
                </Typography>
                <LinearProgress
                  variant={progress.percent > 0 ? 'determinate' : 'indeterminate'}
                  value={progress.percent}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary" mt={0.5} display="block" textAlign="right">
                  {progress.percent}%
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* ── ERROR ── */}
          {error && (
            <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* ── RESULT ── */}
          {result && (
            <Card
              sx={{
                mt: 3,
                background: 'linear-gradient(135deg, #F1F9F4 0%, #E8F5E9 100%)',
                border: '2px solid #1B7340',
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} mb={2}>
                  <CheckCircle sx={{ color: '#1B7340', fontSize: 36 }} />
                  <Typography variant="h5" fontWeight={700} color="success.dark">
                    {T.translationComplete}
                  </Typography>
                </Stack>
                {result.isPartial && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This is a preview translation of your plan limit. Upgrade for the full document.
                  </Alert>
                )}
                <Typography align="center" color="text.secondary" variant="body2" mb={1}>
                  {result.filename}
                </Typography>
                {result.processingTime && (
                  <Typography align="center" variant="body2" color="text.secondary">
                    ⏱ Completed in {formatTime(result.processingTime)}
                    {result.charCount && ` · ${result.charCount.toLocaleString()} chars`}
                  </Typography>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  size="large"
                  sx={{ mt: 2 }}
                  onClick={() => download(result.downloadUrl, result.filename)}
                  startIcon={<Download />}
                >
                  {T.download}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* ── CONFIRM DIALOG ── */}
          <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Warning color="warning" />
                <span>{T.docAssessment}</span>
              </Stack>
            </DialogTitle>
            <DialogContent>
              {quality?.warnings.map((w, i) => <Alert key={i} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
              {quality?.recommendations.map((r, i) => <Alert key={i} severity="info" sx={{ mb: 1 }}>{r}</Alert>)}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDialog(false)}>{T.cancel}</Button>
              <Button variant="contained" onClick={() => { setConfirmDialog(false); if (pendingMode) handleUpload(pendingMode); }}>
                {T.proceedAnyway}
              </Button>
            </DialogActions>
          </Dialog>

        </Container>
      </Box>

      {/* ── FOOTER ── */}
      <Box sx={{ bgcolor: '#1A2744', color: 'rgba(255,255,255,0.8)', py: 3 }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'center', md: 'flex-start' }}
            spacing={2}
          >
            <Box textAlign={{ xs: 'center', md: 'left' }}>
              <Typography fontWeight={700} mb={0.5}>LeaTonga</Typography>
              <Typography variant="caption">
                <a href="mailto:stevenesau2023@gmail.com" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  {T.emailSupport}: stevenesau2023@gmail.com
                </a>
              </Typography>
              <br />
              <Typography variant="caption"><a href="https://wa.me/6767711961" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>WhatsApp: +676 771 1961</a></Typography>
            </Box>
            <Box textAlign={{ xs: 'center', md: 'right' }}>
              <Typography variant="caption" display="block">Developed by <a href="https://stevenesau.com" target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9', fontWeight: 600, textDecoration: 'none' }}>Stevenesau.com</a></Typography>
              <Typography variant="caption" display="block">Powered by <a href="https://tongamarket.com" target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9', fontWeight: 600, textDecoration: 'none' }}>TongaMarket.com</a></Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.6 }}>{T.copyright}</Typography>
            </Box>
          </Stack>
        </Container>
      </Box>
      {/* ── FORGOT PASSWORD DIALOG ──────────────────────────────── */}
      <Dialog open={showForgotDialog} onClose={() => setShowForgotDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {resetToken ? (resetSuccess ? '✅ Password Reset!' : 'Set New Password') : 'Reset Password'}
        </DialogTitle>
        <DialogContent>
          {resetSuccess ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Your password has been reset. You can now log in with your new password.
            </Alert>
          ) : resetToken ? (
            /* Step 2: Enter new password */
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Creating new password for: <strong>{forgotEmail}</strong>
              </Typography>
              <TextField
                fullWidth label="New Password" size="small"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setResetError(''); }}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPassword(!showNewPassword)} size="small" tabIndex={-1}>
                      {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )}}
              />
              <TextField
                fullWidth label="Confirm New Password" size="small"
                type="password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setResetError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleResetPassword()}
              />
              {resetError && <Alert severity="error">{resetError}</Alert>}
            </Stack>
          ) : forgotSent ? (
            /* Step 1b: Sent confirmation — or direct link if SMTP not configured */
            <Box sx={{ mt: 1 }}>
              {directResetUrl ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Email sending is not yet configured. Click the button below to reset your password right now:
                  </Alert>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{ background: '#C8102E', '&:hover': { background: '#a00d25' } }}
                    onClick={() => {
                      const url = new URL(directResetUrl);
                      const tok = url.searchParams.get('reset_token');
                      const em  = url.searchParams.get('email');
                      if (tok && em) {
                        setForgotEmail(decodeURIComponent(em));
                        setResetToken(tok);
                        setForgotSent(false);
                        setDirectResetUrl('');
                        setNewPassword('');
                        setConfirmPassword('');
                        setResetSuccess(false);
                        setResetError('');
                      }
                    }}
                  >
                    🔑 Reset Password Now
                  </Button>
                </Box>
              ) : (
                <Alert severity="success">
                  Reset link sent to <strong>{forgotEmail}</strong>. Check your inbox (and spam folder).
                </Alert>
              )}
            </Box>
          ) : (
            /* Step 1a: Enter email */
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Enter your email and we'll send you a reset link.
              </Typography>
              <TextField
                fullWidth label="Email address" size="small" type="email"
                value={forgotEmail}
                onChange={(e) => { setForgotEmail(e.target.value); setForgotError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleForgotPassword()}
                autoFocus
              />
              {forgotError && <Alert severity="error">{forgotError}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowForgotDialog(false)}>
            {resetSuccess || forgotSent ? 'Close' : 'Cancel'}
          </Button>
          {!forgotSent && !resetSuccess && (
            resetToken ? (
              <Button variant="contained" onClick={handleResetPassword} disabled={resetLoading}
                startIcon={resetLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}>
                {resetLoading ? 'Saving...' : 'Set New Password'}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleForgotPassword} disabled={forgotLoading}
                startIcon={forgotLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}>
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            )
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={showAdmin} onClose={() => setShowAdmin(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1A2744', color: 'white', fontWeight: 700 }}>
          Admin Dashboard
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Open the full Admin Dashboard to view all users, translation stats, error logs, and manage subscriptions.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You will be asked for your admin password on the next page.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAdmin(false)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ bgcolor: '#C8102E', '&:hover': { bgcolor: '#a50d25' } }}
            onClick={() => { window.open('/admin.html', '_blank'); setShowAdmin(false); }}
          >
            Open Admin Dashboard
          </Button>
        </DialogActions>
      </Dialog>

      {/* PWA Install Guide Dialog */}
      <Dialog open={showInstallDialog} onClose={() => setShowInstallDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#C8102E', color: 'white', fontWeight: 700 }}>
          {uiLang === 'en' ? '📲 Install LeaTonga App' : "📲 'Inisitolo e LeaTonga"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Chip label="Android" color="success" sx={{ mr: 1, mb: 1 }} />
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              {uiLang === 'en' ? 'Chrome on Android:' : "Chrome 'i he Android:"}
            </Typography>
            <ol style={{ margin: '8px 0', paddingLeft: 20 }}>
              <li>{uiLang === 'en' ? 'Open Chrome → visit leatonga.tongamarket.com' : "Tohi Chrome → 'alu ki leatonga.tongamarket.com"}</li>
              <li>{uiLang === 'en' ? 'Tap the 3-dot menu ⋮ (top right)' : "Ala ki he menu ⋮ ('i 'olunga to'omata'u)"}</li>
              <li>{uiLang === 'en' ? 'Tap "Add to Home Screen"' : 'Ala ki he "Add to Home Screen"'}</li>
              <li>{uiLang === 'en' ? 'Tap "Add" — done!' : "Ala ki he 'Add' — kakato!"}</li>
            </ol>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Chip label="iPhone / iOS" color="primary" sx={{ mr: 1, mb: 1 }} />
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              {uiLang === 'en' ? 'Safari on iPhone (must use Safari, not Chrome):' : "Safari 'i he iPhone (fiema'u Safari, 'ikai Chrome):"}
            </Typography>
            <ol style={{ margin: '8px 0', paddingLeft: 20 }}>
              <li>{uiLang === 'en' ? 'Open Safari → visit leatonga.tongamarket.com' : "Tohi Safari → 'alu ki leatonga.tongamarket.com"}</li>
              <li>{uiLang === 'en' ? 'Tap Share button 📤 (bottom of screen)' : "Ala ki he pulu Vahevahe 📤 ('i lalo)"}</li>
              <li>{uiLang === 'en' ? 'Tap "Add to Home Screen"' : 'Ala ki he "Add to Home Screen"'}</li>
              <li>{uiLang === 'en' ? 'Tap "Add" — LeaTonga installed!' : "Ala ki he 'Add' — Kuo 'inisitolo!"}</li>
            </ol>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Chip label={uiLang === 'en' ? 'Mac / Desktop' : 'Koniputa'} color="default" sx={{ mr: 1, mb: 1 }} />
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              {uiLang === 'en' ? 'Chrome or Edge on Mac/Windows:' : "Chrome pe Edge 'i he Mac/Windows:"}
            </Typography>
            <ol style={{ margin: '8px 0', paddingLeft: 20 }}>
              <li>{uiLang === 'en' ? 'Open Chrome → visit leatonga.tongamarket.com' : "Tohi Chrome → 'alu ki leatonga.tongamarket.com"}</li>
              <li>{uiLang === 'en' ? 'Click the Install icon ⊕ in the address bar' : "Holoki ki he icon 'Inisitolo ⊕ 'i he va'a tohi"}</li>
              <li>{uiLang === 'en' ? 'Click "Install" — LeaTonga opens as desktop app!' : "Holoki ki he 'Install' — 'Oku 'atuloa 'a e LeaTonga!"}</li>
            </ol>
          </Box>
          <Alert severity="info" sx={{ mt: 1 }}>
            {uiLang === 'en'
              ? 'Works offline after install. Always up to date automatically.'
              : "'Oku ngāue 'i he taimi 'ikai ha 'initaneti. Fakatonutonu fakaʻotometi."}
          </Alert>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button variant="outlined" href="/install" target="_blank" size="small">
              {uiLang === 'en' ? '📄 Full Installation Guide' : "📄 Fakahinohino 'Inisitolo Kakato"}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInstallDialog(false)}>
            {uiLang === 'en' ? 'Close' : 'Fakatāpuni'}
          </Button>
        </DialogActions>
      </Dialog>

    </ThemeProvider>
  );
}
